import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

import { CryptHelper } from '@helper/crypt-helper';
import { PreferenceKey } from '@helper/preference-key';
import { Cart } from '@proto/store/api/store/cart';
import { Token } from '@proto/store/api/auth/resources';
import { RecipientAddress } from '@proto/store/api/common/common';
import { OnlyBrowser } from '@service/only-browser.decorator';
import { AuthStateService } from '@service/auth-state.service';
import { Address, OverseaAddress, Recipient, Recipient_Type, StoreAddress } from '@proto/api/common/common';
import { Customer_Provider } from '@proto/api/customers/resources';

export class ReceiverPhone {
  constructor(
    public type: Recipient_Type,
    public addressId: string,
    public phone: string
  ) {}
}

export class CsUnreadInfo {
  // sessionIds：當從消息中心進入客服後, sessionIds 就是當下所有 records, 全部已讀.
  // lastReplayTime：listNotifications API 的 customerServiceInfo replyTime.
  constructor(
    public sessionIds: string[],
    public lastReplayTime: number
  ) {}
}

@OnlyBrowser()
@Injectable({
  providedIn: 'root',
})
export class SafeStorageService {
  static PLATFORM_ID: object;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private authState: AuthStateService
  ) {
    SafeStorageService.PLATFORM_ID = platformId;
  }

  getLocalItem(key: string): string | undefined {
    try {
      return localStorage?.getItem(key) ?? undefined;
    } catch {
      // ignore
    }
    return undefined;
  }

  setLocalItem(key: string, value: string): void {
    try {
      localStorage?.setItem(key, value);
    } catch {
      // ignore
    }
  }

  removeLocalItem(key: string): void {
    try {
      localStorage?.removeItem(key);
    } catch {
      // ignore
    }
  }

  setCanReadProfile(isVerifyPhoneCode: boolean): void {
    this.setLocalItem(PreferenceKey.CAN_READ_PROFILE, isVerifyPhoneCode ? 't' : '');
  }

  getCanReadProfile(): boolean | undefined {
    return this.getLocalItem(PreferenceKey.CAN_READ_PROFILE) === 't';
  }

  setLoginType(id: Customer_Provider): void {
    this.setLocalItem(PreferenceKey.LOGIN_TYPE, id.toString());
  }

  setProfilePic(url: string): void {
    this.setLocalItem(PreferenceKey.PROFILE_PIC_URL, url);
  }

  getLoginType(): Customer_Provider | undefined {
    const data = this.getLocalItem(PreferenceKey.LOGIN_TYPE);
    if (!data) {
      return undefined;
    }
    const type = Number(data);
    if (!isNaN(type) && Object.values(Customer_Provider).includes(type)) {
      return type;
    }
    return undefined;
  }

  getLocalAddress(): RecipientAddress | undefined {
    const jsonStr = this.getLocalItem(PreferenceKey.ADDRESS);
    if (jsonStr) {
      const dec = CryptHelper.decrypt(jsonStr, this.authState);
      if (!dec) {
        return undefined;
      }
      return RecipientAddress.fromJSON(JSON.parse(dec));
    }
    return undefined;
  }

  setLocalAddress(recipient: RecipientAddress): void {
    this.setLocalItem(
      PreferenceKey.ADDRESS,
      CryptHelper.encrypt(JSON.stringify(RecipientAddress.toJSON(recipient)), this.authState)
    );
  }

  setLoginPhone(phone: string): void {
    this.setLocalItem(PreferenceKey.LOGIN_PHONE, CryptHelper.encrypt(phone, this.authState));
  }

  getLoginPhone(): string {
    const encrypted = this.getLocalItem(PreferenceKey.LOGIN_PHONE);
    return encrypted ? CryptHelper.decrypt(encrypted, this.authState) : '';
  }

  setPartialAddress(
    id?: string,
    homeAddress?: Address,
    storeAddress?: StoreAddress,
    overseaAddress?: OverseaAddress
  ): void {
    const address = RecipientAddress.fromPartial(this.getLocalAddress() ?? {});
    if (!address.recipient) {
      address.recipient = Recipient.fromPartial({});
    }
    address.id = id || address.id;
    if (homeAddress?.name || homeAddress?.phone || homeAddress?.city || homeAddress?.area || homeAddress?.street) {
      address.recipient.address = homeAddress;
    }
    if (storeAddress?.name || storeAddress?.phone || storeAddress?.storeId) {
      address.recipient.storeAddress = storeAddress;
    }
    if (
      overseaAddress?.firstName ||
      overseaAddress?.lastName ||
      overseaAddress?.email ||
      overseaAddress?.phoneCountryCode ||
      overseaAddress?.province ||
      overseaAddress?.city ||
      overseaAddress?.street ||
      overseaAddress?.postcode
    ) {
      address.recipient.overseaAddress = overseaAddress;
    }
    this.setLocalAddress(address);
  }

  clearLocalAddress(): void {
    this.removeLocalItem(PreferenceKey.ADDRESS);
  }

  getToken(): Token | undefined {
    const jsonStr = this.getLocalItem(PreferenceKey.ACCESS_TOKEN);
    return jsonStr ? Token.fromJSON(JSON.parse(jsonStr)) : undefined;
  }

  getFbNonce(): string | undefined {
    return this.getLocalItem(PreferenceKey.FBNONCE);
  }

  setFbNonce(data: string): void {
    this.setLocalItem(PreferenceKey.FBNONCE, data);
  }

  getTopAnnouncement(): string | undefined {
    return this.getLocalItem(PreferenceKey.TOP_ANNOUNCEMENT);
  }

  setTopAnnouncement(data: string): void {
    this.setLocalItem(PreferenceKey.TOP_ANNOUNCEMENT, data);
  }

  getAnnouncement(): string | undefined {
    return this.getLocalItem(PreferenceKey.ANNOUNCEMENT);
  }

  setAnnouncement(data: string): void {
    this.setLocalItem(PreferenceKey.ANNOUNCEMENT, data);
  }

  getFavorites(): string[] | undefined {
    const jsonStr = this.getLocalItem(PreferenceKey.FAVORITE);
    return jsonStr ? JSON.parse(jsonStr) : undefined;
  }

  setFavorites(favorites: string[]): void {
    this.setLocalItem(PreferenceKey.FAVORITE, JSON.stringify(favorites));
  }

  getKeywords(): string[] | undefined {
    const jsonStr = this.getLocalItem(PreferenceKey.KEYWORDS);
    return jsonStr ? JSON.parse(jsonStr) : undefined;
  }

  setKeywords(keywords: string[]): void {
    this.setLocalItem(PreferenceKey.KEYWORDS, JSON.stringify(keywords));
  }

  getNotificationIds(): string[] | undefined {
    const jsonStr = this.getLocalItem(PreferenceKey.NOTIFICATION_IDS);
    return jsonStr ? JSON.parse(jsonStr) : undefined;
  }

  setNotificationIds(ids: string[]): void {
    const all = Array.from(new Set(ids.concat(this.getNotificationIds() ?? [])));
    this.setLocalItem(PreferenceKey.NOTIFICATION_IDS, JSON.stringify(all));
  }

  getCsUnreadInfo(): CsUnreadInfo | undefined {
    const jsonStr = this.getLocalItem(PreferenceKey.CS_UNREAD_INFO);
    return jsonStr ? JSON.parse(jsonStr) : undefined;
  }

  setCsUnreadInfo(info: CsUnreadInfo): void {
    this.setLocalItem(PreferenceKey.CS_UNREAD_INFO, JSON.stringify(info));
  }

  getCart(): Cart | undefined {
    const jsonStr = this.getLocalItem(PreferenceKey.CART);
    return jsonStr ? Cart.fromJSON(JSON.parse(jsonStr)) : undefined;
  }

  setCart(cart: Cart): void {
    this.setLocalItem(PreferenceKey.CART, JSON.stringify(Cart.toJSON(cart)));
  }

  getPhone(type: Recipient_Type, addressId?: string): ReceiverPhone | undefined {
    return this.getPhones()?.find(i => i.type === type && (!addressId || addressId === i.addressId));
  }

  getPhones(): ReceiverPhone[] | undefined {
    const jsonStr = this.getLocalItem(PreferenceKey.PHONE);
    if (!jsonStr) {
      return undefined;
    }
    const receiverPhones: ReceiverPhone[] = JSON.parse(jsonStr);
    receiverPhones.forEach(item => {
      item.phone = CryptHelper.decrypt(item.phone, this.authState);
    });
    return receiverPhones;
  }

  setPhone(data: ReceiverPhone): void {
    let records = this.getPhones();
    if (!records) {
      records = [];
    }
    const index = records.findIndex(record => record.type == data.type && record.addressId == data.addressId);
    if (index >= 0) {
      records[index].phone = data.phone;
    } else {
      records.push(data);
    }
    records.forEach(r => (r.phone = CryptHelper.encrypt(r.phone, this.authState)));
    this.setLocalItem(PreferenceKey.PHONE, JSON.stringify(records));
  }

  getCsLastReadId(): string {
    return this.getLocalItem(PreferenceKey.CS_LAST_READ_ID) || '';
  }

  setCsLastReadId(id: string): void {
    this.setLocalItem(PreferenceKey.CS_LAST_READ_ID, id);
  }

  getOrderSidebarTab(): string {
    return this.getLocalItem(PreferenceKey.ORDER_SIDEBAR_TAB) || '';
  }

  setOrderSidebarTab(tab: string): void {
    this.setLocalItem(PreferenceKey.ORDER_SIDEBAR_TAB, tab);
  }

  clearOrderSidebarTab(): void {
    this.removeLocalItem(PreferenceKey.ORDER_SIDEBAR_TAB);
  }

  setLastSendCodeTime(time: number): void {
    this.setLocalItem(PreferenceKey.LAST_SEND_CODE_TIME, time.toString());
  }

  getLastSendCodeTime(): number | undefined {
    const time = this.getLocalItem(PreferenceKey.LAST_SEND_CODE_TIME);
    return time ? Number(time) : undefined;
  }

  setLastResultOrder(orderId: string): void {
    this.setLocalItem(PreferenceKey.LAST_RESULT_ORDER, orderId);
  }

  getLastResultOrder(): string {
    return this.getLocalItem(PreferenceKey.LAST_RESULT_ORDER) || '';
  }
}
